import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  X, 
  Code, 
  Terminal, 
  ShieldCheck, 
  Cpu, 
  BookOpen, 
  CheckCircle2, 
  Copy, 
  ExternalLink,
  Lock,
  Zap,
  Box,
  Search,
  MessageSquare
} from 'lucide-react';

const DocModal = ({ type, onClose, t, onContactSupport }) => {
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const sections = {
    sdk: {
      icon: <Box size={24} />,
      title: t('sdkReference'),
      color: 'primary',
      content: (
        <div className="space-y-8">
          <section className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                 <Terminal size={18} className="text-primary" /> {t('npmInstall')}
              </h3>
              <div className="relative group">
                <pre className="bg-black/40 p-5 rounded-2xl border border-white/5 overflow-x-auto text-sm font-mono text-slate-300">
                  npm i viewgate-wrapper@latest
                </pre>
                <button onClick={() => handleCopy('npm i viewgate-wrapper@latest')} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary">
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                 <Terminal size={18} className="text-primary" /> {t('step2UnifiedSetup')}
              </h3>
              <div className="relative group">
                <pre className="bg-black/40 p-5 rounded-2xl border border-white/5 overflow-x-auto text-sm font-mono text-slate-300">
                  npx viewgate-wrapper setup
                </pre>
                <button onClick={() => handleCopy('npx viewgate-wrapper setup')} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary">
                  <Copy size={16} />
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 italic">
               * The CLI now supports <strong>Auto-Framework Detection</strong> (Vite, Webpack, Next.js).
            </p>
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
               <Zap size={18} className="text-primary" /> {t('newFeatures')}
            </h3>
            <div className="grid grid-cols-1 gap-4">
               <div className="p-4 rounded-2xl bg-white/5 border border-white/5 flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0"><Search size={20} /></div>
                  <div>
                    <h5 className="text-sm font-bold text-slate-200">{t('searchableEndpoints')}</h5>
                    <p className="text-xs text-slate-500 leading-relaxed">{t('searchableEndpointsDesc')}</p>
                  </div>
               </div>
               <div className="p-4 rounded-2xl bg-white/5 border border-white/5 flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0"><CheckCircle2 size={20} /></div>
                  <div>
                    <h5 className="text-sm font-bold text-slate-200">{t('dynamicFocusAreas')}</h5>
                    <p className="text-xs text-slate-500 leading-relaxed">{t('dynamicFocusAreasDesc')}</p>
                  </div>
               </div>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-white/5 border border-white/5 space-y-2">
              <div className="text-primary"><Zap size={20} /></div>
              <h4 className="font-bold text-slate-200">{t('autoCapture')}</h4>
              <p className="text-xs text-slate-500">{t('autoCaptureDesc')}</p>
            </div>
            <div className="p-5 rounded-2xl bg-white/5 border border-white/5 space-y-2">
              <div className="text-emerald-500"><CheckCircle2 size={20} /></div>
              <h4 className="font-bold text-slate-200">{t('offlineReady')}</h4>
              <p className="text-xs text-slate-500">{t('offlineReadyDesc')}</p>
            </div>
          </div>
        </div>
      )
    },
    mcp: {
      icon: <Cpu size={24} />,
      title: t('mcpConfiguration'),
      color: 'purple-500',
      content: (
        <div className="space-y-8">
          <div className="p-6 rounded-3xl bg-purple-500/10 border border-purple-500/20">
            <p className="text-sm text-purple-200 leading-relaxed">
              {t('mcpIntro')} <span className="font-bold text-white">{t('localizedIntelligence')}</span>
            </p>
          </div>

          <section className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                 <Terminal size={18} className="text-purple-500" /> {t('npmInstall')}
              </h3>
              <div className="bg-black/40 p-5 rounded-2xl border border-white/5 font-mono text-sm text-slate-300">
                npm i viewgate-wrapper@latest
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                 <Terminal size={18} className="text-purple-500" /> {t('step2UnifiedSetup')}
              </h3>
              <div className="bg-black/40 p-5 rounded-2xl border border-white/5 font-mono text-sm text-slate-300">
                npx viewgate-wrapper setup
              </div>
              <p className="text-xs text-slate-500 italic">{t('step2UnifiedSetupDesc')}</p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                 <Terminal size={18} className="text-purple-500" /> {t('step3Configuration')}
              </h3>
              <div className="p-4 rounded-2xl bg-white/5 border border-white/5 border-dashed mb-4">
                <p className="text-xs text-slate-400 leading-relaxed">{t('mcpConfigDesc')}</p>
              </div>
              
              <div className="relative group">
                <div className="absolute -top-3 left-4 px-2 py-0.5 bg-slate-900 border border-white/10 rounded text-[9px] font-black uppercase tracking-widest text-slate-500 z-10">mcp-config.json</div>
                <pre className="bg-black/40 p-5 rounded-2xl border border-white/5 overflow-x-auto text-[11px] font-mono text-slate-300 leading-relaxed">
{`{
  "mcpServers": {
    "viewgate": {
      "command": "npx",
      "args": ["-y", "viewgate-mcp@latest"],
      "env": {
        "VIEWGATE_API_KEY": "YOUR_PROJECT_API_KEY",
        "VIEWGATE_PERSONAL_KEY": "YOUR_PERSONAL_KEY"
      }
    }
  }
}`}
                </pre>
                <button 
                  onClick={() => handleCopy(`{
  "mcpServers": {
    "viewgate": {
      "command": "npx",
      "args": ["-y", "viewgate-mcp@latest"],
      "env": {
        "VIEWGATE_API_KEY": "YOUR_PROJECT_API_KEY",
        "VIEWGATE_PERSONAL_KEY": "YOUR_PERSONAL_KEY"
      }
    }
  }
}`)} 
                  className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-purple-500"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-100 uppercase tracking-widest text-[10px] opacity-50">{t('availableTools')}</h3>
            <div className="space-y-4">
                {[
                  { name: 'sync_endpoints', desc: t('syncEndpointsDesc') },
                  { name: 'get_annotations', desc: t('getAnnotationsDesc') },
                  { name: 'planning', desc: t('planningDesc') },
                  { name: 'mark_annotation_ready', desc: t('markReadyDesc') }
                ].map(tool => (
                 <div key={tool.name} className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                    <div className="text-purple-500"><Code size={20} /></div>
                    <div>
                       <h5 className="text-xs font-bold text-slate-200 font-mono">{tool.name}</h5>
                       <p className="text-[11px] text-slate-500">{tool.desc}</p>
                    </div>
                 </div>
               ))}
            </div>
          </section>

          <div className="rounded-2xl border border-white/5 p-4 bg-white/[0.02]">
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">{t('keyBenefits')}</h4>
            <ul className="space-y-3">
              {[
                'automatedBugFixing',
                'contextAwareRefactoring',
                'directUiToCodeSync',
                'aiDrivenPlanning',
                'capacityPrediction'
              ].map((benefitKey) => (
                <li key={benefitKey} className="flex items-center gap-3 text-sm text-slate-300">
                  <CheckCircle2 size={16} className="text-purple-500" /> {t(benefitKey)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )
    },
    security: {
      icon: <ShieldCheck size={24} />,
      title: t('securityWhitepaper'),
      color: 'emerald-500',
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Lock size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-100">{t('dataPrivacy')}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {t('dataPrivacyDesc')}
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-100">{t('compliance')}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {t('complianceDesc')}
              </p>
            </div>
          </div>

          <div className="space-y-6 pt-6 border-t border-white/5">
             <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">{t('securityArchitecture')}</h3>
             <div className="space-y-3">
                {[
                  { title: t('isolatedWorkspace'), desc: t('isolatedWorkspaceDesc') },
                  { title: t('rbacAuth'), desc: t('rbacAuthDesc') },
                  { title: t('sourceTraceability'), desc: t('sourceTraceabilityDesc') }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                    <div className="mt-1"><CheckCircle2 size={16} className="text-emerald-500" /></div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-200">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        </div>
      )
    }
  };

  const current = sections[type] || sections.sdk;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-950/80 backdrop-blur-md cursor-pointer" />
      <motion.div
         initial={{ opacity: 0, scale: 0.9, y: 30 }}
         animate={{ opacity: 1, scale: 1, y: 0 }}
         exit={{ opacity: 0, scale: 0.9, y: 30 }}
         className="bg-[#0f172a] border border-white/10 rounded-[40px] w-full max-w-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
          <div className={`flex items-center gap-4`}>
            <div className={`p-4 rounded-[20px] shadow-lg ${
              current.color === 'primary' ? 'bg-primary/10 text-primary shadow-primary/5' :
              current.color === 'purple-500' ? 'bg-purple-500/10 text-purple-500 shadow-purple-500/5' :
              'bg-emerald-500/10 text-emerald-500 shadow-emerald-500/5'
            }`}>
              {current.icon}
            </div>
            <div>
              <h2 className="text-2xl font-display font-black text-slate-100">{current.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{t('versionStable')}</span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-3 rounded-2xl bg-white/5 text-slate-400 hover:text-white transition-all hover:bg-white/10"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar">
          {current.content}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-white/5 bg-black/20 flex flex-col sm:flex-row items-center justify-between gap-6">
          <button 
            onClick={onContactSupport}
            className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 text-xs font-bold text-slate-300 hover:text-white hover:bg-white/10 transition-all group/contact"
          >
            <div className="p-2 rounded-xl bg-primary/10 text-primary group-hover/contact:bg-primary group-hover/contact:text-white transition-colors">
              <MessageSquare size={16} />
            </div>
            <span>{t('contact_support')}</span>
          </button>
          <a 
            href="https://www.npmjs.com/package/viewgate-wrapper" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs font-bold text-slate-300 hover:text-white transition-colors"
          >
            <BookOpen size={16} /> {t('fullDocs')} <ExternalLink size={14} />
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default DocModal;
